import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";

import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { IAddress, IPlace, makeEmptyPlace } from "../../models/Models";
import { litIf } from "../LitUtils";
import { styles } from "./BulkImport.style";
import merge from "lodash-es/merge.js";
import cloneDeep from "lodash-es/cloneDeep.js";
import { PlaceTable } from "../../backend/cloud/PlaceTable";
import { getClosestPlaces, normalizeLocation } from "../../models/ILocation";
import { reverseGeocode } from "../../Geocode";

import "../FixedPanel/FixedPanel";
import { LocalStorageProperty } from "../../LocalStorageProperty";
import { Geo } from "../../geo/Geo";
import { geoJSONToPlaceDeltas } from "../../PlaceUtils";

@customElement("ds-bulk-import")
export class BulkImport extends BaseElement {
    static override styles = [styles];

    @state() protected _places?: IPlace[];
    @state() protected _importPlaces?: any[];
    @state() protected _currentPlace?: IPlace;
    @state() protected _currentIndex?: number;
    @state() protected _closestPlace?: { place: IPlace; dist: number };
    @state() protected _currentAddress?: IAddress;
    @state() protected _autoDiscardPerfectMatches = true;
    @state() protected _defaultPlace = makeEmptyPlace();
    @state() protected _ignoreDescription = false;

    private _placesTable = this._kernel.dataStore.getSource<PlaceTable>("places");
    private _savedDefaultPlace = new LocalStorageProperty(
        this._kernel.events,
        "bulk-import-current-defaults",
        1
    );

    constructor() {
        super();
        this._defaultPlace = this._makeDefaultPlace();

        const defaults = this._savedDefaultPlace.get();
        if (defaults) {
            this._defaultPlace = defaults;
        }
        import("../PlaceEditor/PlaceEditor");
    }

    private _currentBulkImport = new LocalStorageProperty(
        this._kernel.events,
        "current-bulk-import",
        1,
        undefined,
        false
    );

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._importPlaces = this._currentBulkImport.get();
        if (this._importPlaces) {
            await this._finalizeImports();
            await this._setCurrentIndex(0);
        }
    }

    private async _finalizeImports() {
        this._places = [];
        if (this._importPlaces) {
            for (const place of this._importPlaces) {
                const newPlace = makeEmptyPlace();
                newPlace.data = cloneDeep(this._defaultPlace.data);
                merge(newPlace.data, place);

                if (this._ignoreDescription) {
                    newPlace.data.spelioData.notes = this._defaultPlace.data.spelioData.notes;
                }

                newPlace.data.location = normalizeLocation(newPlace.data.location);
                this._places.push(newPlace);
            }
        }
    }

    private async _fileImportPlaces(inputFiles: FileList | File[]) {
        // find list of closest places
        for (const file of inputFiles) {
            let places = [];
            console.log(file.type);
            const text = await file.text();
            if (file.type === "application/json") {
                places = JSON.parse(text);
            } else if (file.type === "application/vnd.google-earth.kml+xml") {
                const geoJson = Geo.createFromKMLString(text);
                if (geoJson) {
                    places = geoJSONToPlaceDeltas(geoJson);
                }
            }

            for (const place of places) {
                if (!this._importPlaces) {
                    this._importPlaces = [];
                }

                const location = normalizeLocation(place.location);
                const closestPlaces = getClosestPlaces(
                    await this._placesTable.all(),
                    location,
                    1,
                    1
                );
                let include = true;
                if (
                    this._autoDiscardPerfectMatches &&
                    closestPlaces.length > 0 &&
                    closestPlaces[0].dist < 0.5 &&
                    closestPlaces[0].place.data.name === place.name
                ) {
                    include = false;
                }
                if (include) {
                    this._importPlaces.push(place);
                }
            }
        }
        if (this._importPlaces && this._importPlaces?.length > 0) {
            await this._finalizeImports();
            this._saveCurrentProgress();
            await this._setCurrentIndex(0);
        }
    }

    private async _next() {
        if (this._currentIndex !== undefined && this._places) {
            let n = this._currentIndex + 1;
            if (n === this._places?.length) {
                n = 0;
            }
            await this._setCurrentIndex(n);
        }
    }

    private async _prev() {
        if (this._currentIndex !== undefined && this._places) {
            let n = this._currentIndex - 1;
            if (n < 0) {
                n = this._places?.length - 1;
            }
            await this._setCurrentIndex(n);
        }
    }

    private async _setCurrentIndex(n: number) {
        if (this._places && n < this._places.length) {
            this._currentIndex = n;
            this._currentPlace = this._places[n];
            const location = this._currentPlace.data.location;
            const closestPlaces = getClosestPlaces(await this._placesTable.all(), location, 8, 500);
            this._closestPlace = closestPlaces[0];
            this._currentAddress = await reverseGeocode(location);
            this._raiseEvent("ds-view-on-map", { location });
        }
    }

    private _discardCurrentFile() {
        if (
            this._currentIndex !== undefined &&
            this._places &&
            this._currentPlace &&
            this._importPlaces
        ) {
            this._places.splice(this._currentIndex, 1);
            this._importPlaces.splice(this._currentIndex, 1);
            this._saveCurrentProgress();
            if (this._places.length === 0) {
                this._reset();
            } else {
                if (this._currentIndex === this._places.length) {
                    this._currentIndex = 0;
                }
                this._setCurrentIndex(this._currentIndex);
            }
        }
    }

    private _saveCurrentProgress() {
        this._currentBulkImport.set(this._importPlaces);
    }

    private _reset() {
        this._currentPlace = undefined;
        this._currentIndex = undefined;
        this._places = undefined;
        this._importPlaces = undefined;
        this._currentAddress = undefined;
        this._currentBulkImport.reset();
    }

    private _makeDefaultPlace() {
        const p = makeEmptyPlace();
        p.data.locationKnown = true;
        p.data.checked = true;
        return p;
    }

    private _resetDefaults() {
        this._setDefaultPlace(this._makeDefaultPlace());
    }

    private _setDefaultPlace(place: IPlace) {
        if (this._currentIndex !== undefined) {
            this._defaultPlace = place;
            this._savedDefaultPlace.set(this._defaultPlace);
            this._finalizeImports();
            this._setCurrentIndex(this._currentIndex);
            this.requestUpdate();
        }
    }
    private async _importCurrentPlace() {
        if (this._currentPlace) {
            this._placesTable.create(this._currentPlace);
            this._discardCurrentFile();
        }
    }

    override render() {
        return html`<ds-fixed-panel>
                <div slot="header">
                    ${litIf(
                        !!this._places,
                        () => html`
                            <div class="button-bar">
                                <sl-icon-button
                                    name="arrow-left-square"
                                    @click=${async () => await this._prev()}
                                ></sl-icon-button>
                                <span style="flex-grow: 1;text-align: center; font-size:1.3em">
                                    ${this._currentIndex !== undefined
                                        ? `${this._currentIndex + 1} / ${this._places?.length}`
                                        : ``}
                                </span>
                                <sl-icon-button
                                    name="arrow-right-square"
                                    @click=${async () => await this._next()}
                                ></sl-icon-button>
                            </div>
                        `
                    )}
                </div>
                <div slot="body" class="flex-column">
                    ${litIf(
                        !this._places,
                        () => html`
                            <sl-button
                                @click=${() =>
                                    this.shadowRoot?.getElementById("media-import-input")?.click()}
                                >Browse for files</sl-button
                            >
                            <sl-checkbox
                                ?checked=${this._autoDiscardPerfectMatches}
                                @sl-change=${(e: any) =>
                                    (this._autoDiscardPerfectMatches = e.target.checked)}
                                >Auto discard perfect matches</sl-checkbox
                            >
                            <sl-checkbox
                                ?checked=${this._ignoreDescription}
                                @sl-change=${(e: any) =>
                                    (this._ignoreDescription = e.target.checked)}
                                >Ignore place descriptions</sl-checkbox
                            >
                        `,
                        () => html`
                            <h3>${this._currentPlace?.data.name}</h3>
                            ${litIf(
                                this._currentAddress,
                                () =>
                                    html`<span>
                                        ${this._currentAddress?.state},
                                        ${this._currentAddress?.country}
                                    </span>`
                            )}
                            ${litIf(
                                this._closestPlace,
                                () =>
                                    html`<span
                                        >${this._closestPlace?.dist.toFixed(0)}m from
                                        ${this._closestPlace?.place.data.name}</span
                                    >`
                            )}
                            <sl-tab-group>
                                <sl-tab slot="nav" panel="current">Current</sl-tab>
                                <sl-tab slot="nav" panel="defaults">Defaults</sl-tab>

                                <sl-tab-panel name="current">
                                    <ds-place-editor
                                        class="sl-theme-light"
                                        id="editor"
                                        .place=${this._currentPlace}
                                        showdelete
                                        noMediaGallery
                                        noCommandBar
                                        @on-modified=${(event: CustomEvent) =>
                                            (this._currentPlace = event.detail.place)}
                                    ></ds-place-editor>
                                </sl-tab-panel>

                                <sl-tab-panel name="defaults">
                                    <ds-place-editor
                                        class="sl-theme-light"
                                        id="editor"
                                        .place=${this._defaultPlace}
                                        showdelete
                                        noMediaGallery
                                        noCommandBar
                                        @on-modified=${(event: CustomEvent) =>
                                            this._setDefaultPlace(event.detail.place)}
                                    ></ds-place-editor>
                                </sl-tab-panel>
                            </sl-tab-group>
                        `
                    )}
                </div>
                <div slot="footer">
                    <div class="button-bar flex-row">
                        ${litIf(
                            this._currentPlace,
                            () => html`
                                <div>
                                    <sl-button
                                        size="medium"
                                        @click=${() => {
                                            this._resetDefaults();
                                        }}
                                        >Reset defaults</sl-button
                                    >
                                </div>
                                <div>
                                    <sl-button
                                        size="medium"
                                        variant="danger"
                                        @click=${() => {
                                            this._reset();
                                        }}
                                        >Clear all</sl-button
                                    >
                                    <sl-button
                                        size="medium"
                                        variant="warning"
                                        @click=${() => this._discardCurrentFile()}
                                        >Discard</sl-button
                                    >
                                    <sl-button
                                        size="medium"
                                        variant="success"
                                        @click=${() => this._importCurrentPlace()}
                                        >Import</sl-button
                                    >
                                </div>
                            `
                        )}
                    </div>
                </div>
            </ds-fixed-panel>
            <input
                id="media-import-input"
                type="file"
                multiple
                @change=${(event: any) => this._fileImportPlaces(event.target?.files)}
                style="visibility:collapse; display:none"
                accept=".kml,application/json"
            /> `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-bulk-import": BulkImport;
    }
}
